import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type {
  CalendarDay,
  DashboardEvent,
  DashboardStats,
  DashboardStatsResult,
  FilterOption,
  PipelineStage,
  RecentActivityItem,
} from "@/components/dashboard/dashboardApiMappers";
import { DEFAULT_DASHBOARD_DATE_RANGE } from "@/config";

export interface DashboardState {
  stats: DashboardStats | null;
  rangeOptions: FilterOption[];
  selectedRangeSlug: string;
  todayEvents: DashboardEvent[];
  upcomingEvents: DashboardEvent[];
  pipeline: PipelineStage[];
  /** The month currently held in `calendarDays`, `YYYY-MM`. */
  calendarMonth: string;
  calendarDays: CalendarDay[];
  recentActivity: RecentActivityItem[];
  loading: boolean;
  /** True while the date-filter re-fetch of `stats` is in flight. */
  statsLoading: boolean;
}

const initialState: DashboardState = {
  stats: null,
  rangeOptions: [],
  selectedRangeSlug: DEFAULT_DASHBOARD_DATE_RANGE,
  todayEvents: [],
  upcomingEvents: [],
  pipeline: [],
  calendarMonth: "",
  calendarDays: [],
  recentActivity: [],
  loading: true,
  statsLoading: false,
};

/** One-shot payload the Initializer hydrates from the SSR fetchers. */
export interface DashboardHydratePayload {
  stats: DashboardStats;
  rangeOptions: FilterOption[];
  selectedRangeSlug: string;
  todayEvents: DashboardEvent[];
  upcomingEvents: DashboardEvent[];
  pipeline: PipelineStage[];
  calendarMonth: string;
  calendarDays: CalendarDay[];
  recentActivity: RecentActivityItem[];
}

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    hydrateDashboard(state, action: PayloadAction<DashboardHydratePayload>) {
      const p = action.payload;
      state.stats = p.stats;
      state.rangeOptions = p.rangeOptions;
      state.selectedRangeSlug = p.selectedRangeSlug;
      state.todayEvents = p.todayEvents;
      state.upcomingEvents = p.upcomingEvents;
      state.pipeline = p.pipeline;
      state.calendarMonth = p.calendarMonth;
      state.calendarDays = p.calendarDays;
      state.recentActivity = p.recentActivity;
    },
    /** Replace the calendar density map when the user navigates months. */
    setCalendarDays(
      state,
      action: PayloadAction<{ month: string; days: CalendarDay[] }>,
    ) {
      state.calendarMonth = action.payload.month;
      state.calendarDays = action.payload.days;
    },
    setDashboardLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setDashboardStatsResult(
      state,
      action: PayloadAction<DashboardStatsResult>,
    ) {
      const payload = action.payload;
      if (!payload) return;
      state.stats = payload.stats;
      if (payload.rangeOptions?.length) {
        state.rangeOptions = payload.rangeOptions;
      }
      if (payload.range) {
        state.selectedRangeSlug = payload.range;
      }
    },
    setSelectedRangeSlug(state, action: PayloadAction<string>) {
      state.selectedRangeSlug = action.payload;
    },
    setDashboardStatsLoading(state, action: PayloadAction<boolean>) {
      state.statsLoading = action.payload;
    },
  },
});

export const {
  hydrateDashboard,
  setCalendarDays,
  setDashboardLoading,
  setDashboardStatsResult,
  setSelectedRangeSlug,
  setDashboardStatsLoading,
} = dashboardSlice.actions;

export default dashboardSlice.reducer;
